// src/pages/CourseTime/components/SessionAssessmentViewer.comp.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { GET_APP_LOCALIZE_TEXT } from '../../../utils/dq_lc_service/lc_service';

// Styled components
const AssessmentContainer = styled.div`
  width: 100%;
  padding: 20px;
  font-family: "Source Sans Pro", "Helvetica Neue";
`;

const AssessmentTitle = styled.h2`
  text-align: center;
  font-weight: 700;
  color: #ff6468;
  font-size: 28px;
  margin-bottom: 20px;
`;

const QuestionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SubtitleTop = styled.div`
  color: #fe696e;
  font-weight: 700;
  text-align: center;
  margin-bottom: 8px;
  font-size: 1.5rem;
`;

const QuestionText = styled.h3`
  color: #444;
  font-weight: 700;
  text-align: center;
  font-size: 1.5rem;
  margin: 0;
`;

const SubtitleBottom = styled.div`
  color: #0097b2;
  font-weight: 700;
  text-align: center;
  margin-top: 8px;
  font-size: 1.5rem;
`;

const OptionsContainer = styled.div`
  margin-top: 100px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  position: relative;
  z-index: 10;
`;

const OptionsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  width: 100%;
`;

const OptionButton = styled.div`
  cursor: pointer;
  padding: 12px 16px;
  border-radius: 8px;
  border: 2px solid #fe696e;
  min-width: 120px;
  max-width: 360px;
  text-align: center;
  background-color: ${props => props.selected ? '#fe696e' : '#fff'};
  color: ${props => props.selected ? '#fff' : '#000'};
  flex: 1 1 200px;
  pointer-events: auto;
  position: relative;
  z-index: 10;
  transition: all 0.2s ease;
  font-size: 16px;
  
  &:hover {
    opacity: 0.8;
  }
`;

const NoQuestionsText = styled.p`
  text-align: center;
  color: #666;
  font-size: 16px;
`;

const SessionAssessmentViewer = ({ title, items, currentQuestionIndex, pageLc }) => {
    const [questions, setQuestions] = useState([]);

    useEffect(() => {
        // Map raw items to question objects
        const mapped = (items || []).map((item, idx) => {
            let options = [];
            try {
                options = typeof item.instrument_response === 'string' ? JSON.parse(item.instrument_response) : item.instrument_response;
                if (!Array.isArray(options)) options = [options];
            } catch (e) {
                options = [];
            }
            return {
                id: idx + 1,
                type: item.category_11 || 'MCQ',
                instrument_question: item.instrument_question || '—',
                options,
                question_subtitle_top: item.question_subtitle_top,
                question_subtitle_bottom: item.question_subtitle_bottom,
                userAnswer: (item.category_11 === 'MAQ') ? [] : null,
            };
        });
        setQuestions(mapped);
    }, [items]);

  const handleOptionSelect = (option) => {
    setQuestions(prev => {
      const updated = prev.map((q, i) => {
        if (i !== currentQuestionIndex) return q;
        const updatedQuestion = { ...q };
        if (q.type === 'MCQ' || q.type === 'LSQ' || q.type === 'YNQ') {
          updatedQuestion.userAnswer = option;
        } else if (q.type === 'MAQ') {
          updatedQuestion.userAnswer = updatedQuestion.userAnswer?.includes(option)
            ? updatedQuestion.userAnswer.filter(o => o !== option)
            : [...(updatedQuestion.userAnswer || []), option];
        }
        return updatedQuestion;
      });
      return updated;
    });
  };

  const currentQuestion = questions[currentQuestionIndex] || null;

    const isFreeTextQuestion = (q) => q && q.type === 'OPQ' && Array.isArray(q.options) && q.options.includes('Free text');

    const isOptionSelected = useCallback((option) => {
        if (!currentQuestion) return false;
        if (currentQuestion.type === 'MCQ' || currentQuestion.type === 'LSQ' || currentQuestion.type === 'YNQ') return currentQuestion.userAnswer === option;
        if (currentQuestion.type === 'MAQ') return currentQuestion.userAnswer?.includes(option);
        return false;
    }, [currentQuestion]);

    return (
        <AssessmentContainer>
            {/* Question block */}
            {currentQuestion ? (
                <QuestionContainer>
                    {currentQuestion.question_subtitle_top && (
                        <SubtitleTop>{currentQuestion.question_subtitle_top}</SubtitleTop>
                    )}
                    <QuestionText>{currentQuestion.instrument_question}</QuestionText>
                    {currentQuestion.question_subtitle_bottom && (
                        <SubtitleBottom>{currentQuestion.question_subtitle_bottom}</SubtitleBottom>
                    )}

                    {/* Options */}
                    <OptionsContainer>
                        <OptionsGrid>
                            {(currentQuestion.options || []).map((opt, idx) => (
                                <OptionButton
                                    key={idx}
                                    selected={isOptionSelected(opt)}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        console.log('Option clicked:', opt, 'Index:', idx);
                                        handleOptionSelect(opt);
                                    }}
                                >
                                    {String(opt)}
                                </OptionButton>
                            ))}
                        </OptionsGrid>
                    </OptionsContainer>
                </QuestionContainer>
            ) : (
                <NoQuestionsText>{GET_APP_LOCALIZE_TEXT(pageLc, 'ct__no_questions_available')}</NoQuestionsText>
            )}
        </AssessmentContainer>
    );
};

export default SessionAssessmentViewer;
